import {
  invalidateDocumentListData,
  type SimpleRole,
} from "@prismicio/editor-fields"
import { Button, Tooltip, useAlert } from "@prismicio/editor-ui"
import { useQueryClient } from "@tanstack/react-query"
import { useTransition } from "react"

import {
  type DocumentVersionStatus,
  unpublishDocument,
} from "../../../app/services/core"
import { unpublishDocumentPermission } from "../../../app/services/permissions"
import { useMyRole } from "../../../app/services/role"
import { revalidateDocumentMeta } from "../../documentData"
import { useExistingDocument } from "../../DocumentProvider"
import { actionAlert, type PendingActionProps } from "./HeaderButtons"

interface UnpublishButtonProps extends PendingActionProps {}

export function UnpublishButton(props: UnpublishButtonProps) {
  const { pendingAction } = props
  const { disabled, unpublish, roleCanUnpublish, tooltip } =
    useUnpublishButton(props)

  return (
    <Tooltip
      visible={!roleCanUnpublish}
      title={tooltip.title}
      content={tooltip.content}
    >
      <Button
        size="large"
        loading={pendingAction === "unpublish"}
        disabled={disabled}
        onClick={unpublish}
      >
        Unpublish
      </Button>
    </Tooltip>
  )
}

function useUnpublishButton(props: UnpublishButtonProps) {
  const { pendingAction, onPendingActionChange } = props
  const { data, edited, status, locale, teamSpace } = useExistingDocument()

  const { meta } = data

  const queryClient = useQueryClient()

  const { showAlert } = useAlert()
  const [isUpdating, startTransition] = useTransition()

  const role = useMyRole({ localeId: locale, teamSpaceId: teamSpace.id })
  const roleCanUnpublish = unpublishDocumentPermission[role]

  const disabled = Boolean(
    edited || pendingAction || isUpdating || !roleCanUnpublish,
  )

  function unpublish() {
    if (!showUnpublish(status)) return

    onPendingActionChange("unpublish")

    unpublishDocument(meta.id, status)
      .then(() => {
        startTransition(() => void revalidateDocumentMeta(meta.id))

        void invalidateDocumentListData(queryClient)
      })
      .catch(() =>
        actionAlert({ action: "unpublish", showAlert, onRetry: unpublish }),
      )
      .finally(() => onPendingActionChange(undefined))
  }

  return {
    disabled,
    unpublish,
    roleCanUnpublish,
    tooltip: getTooltip(role),
  }
}

export function showUnpublish(
  version: DocumentVersionStatus,
): version is "published" {
  return version === "published"
}

function getTooltip(role: SimpleRole) {
  if (role === "Readonly")
    return {
      title: "You can't update documents",
      content:
        "Only users with the role of publisher or administrator can update documents.",
    }

  return {
    title: "You can't unpublish",
    content:
      "Only users with the role of publisher or administrator can unpublish documents.",
  }
}
